import { useState, useCallback, useRef } from "react";
import { classifyStreamingError, logStreamingError, ERROR_TYPES } from "../utils/errorClassifier";

const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff delays

/**
 * Custom hook for handling streaming chat functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.showSnackbar - Function to show notifications
 * @param {Function} options.updateTokenUsage - Function to update token usage
 * @returns {Object} Streaming chat methods and state
 */
const useStreamingChat = ({ showSnackbar, updateTokenUsage }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const abortControllerRef = useRef(null);
  const lastRequestRef = useRef(null);

  /**
   * Stops the current streaming response
   */
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setStreamingMessageId(null);
    setRetryAttempt(0);
  }, []);

  /**
   * Handles streaming chat responses
   * @param {Object} options - Stream options
   * @param {string} options.endpoint - API endpoint
   * @param {Object} options.headers - Request headers
   * @param {Object} options.body - Request body
   * @param {Function} options.onStart - Callback when streaming starts
   * @param {Function} options.onChunk - Callback for each text chunk
   * @param {Function} options.onComplete - Callback when streaming completes
   * @param {Function} options.onError - Callback when error occurs
   * @param {boolean} options.streamingEnabled - Whether streaming is enabled
   * @returns {Promise<Object|null>} Final response or null if aborted
   */
  const streamChat = useCallback(async ({
    endpoint,
    headers,
    body,
    onStart,
    onChunk,
    onComplete,
    onError,
    streamingEnabled = true,
    isRetry = false
  }) => {
    if (!isRetry) {
      // Store request parameters for potential retry
      lastRequestRef.current = {
        endpoint,
        headers,
        body,
        onStart,
        onChunk,
        onComplete,
        onError
      };
    }

    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

    // If streaming is disabled, use traditional fetch
    if (!streamingEnabled) {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: isFormData
            ? { ...headers }
            : { "Content-Type": "application/json", ...headers },
          body: isFormData ? body : JSON.stringify(body),
        });

        if (!response.ok) {
          const err = new Error(`Error: ${response.status} ${response.statusText}`);
          err.status = response.status;
          try {
            err.payload = await response.json();
          } catch {
            // non-JSON error body — leave payload undefined
          }
          throw err;
        }

        const data = await response.json();
        return data;
      } catch (error) {
        const { type, message } = classifyStreamingError(error);
        logStreamingError(error, { type, endpoint });
        onError?.({ type, message, retryable: true, status: error.status, payload: error.payload });
        showSnackbar(message, "error");
        return null;
      }
    }

    // Create a new AbortController for this stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      // Set streaming state
      setIsStreaming(true);
      const messageId = Date.now().toString();
      setStreamingMessageId(messageId);

      // Call onStart with the message ID
      onStart?.(messageId);

      // Add streaming flag and timeout to body — different mechanism per body type
      let requestBody;
      if (isFormData) {
        // Mutate the same FormData instance; caller passes a fresh one per request.
        if (!body.has("streaming")) body.append("streaming", "true");
        if (!body.has("stream_timeout")) body.append("stream_timeout", "30");
        requestBody = body;
      } else {
        requestBody = { ...body, streaming: true, stream_timeout: 30 };
      }

      // Fetch with streaming
      const response = await fetch(endpoint, {
        method: "POST",
        headers: isFormData
          ? { ...headers }
          : { "Content-Type": "application/json", ...headers },
        body: isFormData ? requestBody : JSON.stringify(requestBody),
        signal,
      });

      if (!response.ok) {
        const err = new Error(`Error: ${response.status} ${response.statusText}`);
        err.status = response.status;
        try {
          err.payload = await response.json();
        } catch {
          // non-JSON error body — leave payload undefined
        }
        throw err;
      }

      // Process the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let completeResponse = "";
      let finalResponse = null;
      let buffer = "";

      while (true) {
        try {
          const { value, done } = await reader.read();
          
          if (done) break;

          // Decode the chunk
          const chunkText = decoder.decode(value, { stream: true });
          buffer += chunkText;
          
          // Process complete JSON objects from the buffer
          let processedToIndex = 0;
          
          // Look for JSON objects separated by newlines
          while (true) {
            const endIndex = buffer.indexOf('\n', processedToIndex);
            if (endIndex === -1) break; // No more complete objects
            
            const jsonStr = buffer.substring(processedToIndex, endIndex).trim();
            processedToIndex = endIndex + 1;
            
            if (!jsonStr) continue; // Skip empty lines
            
            try {
              // Parse the JSON chunk
              const chunk = JSON.parse(jsonStr);
              
              // Handle different chunk types
              if (chunk.type === 'status') {
                // Pipeline status chunk - pass to callback for UI display
                onChunk?.(chunk);
              } else if (chunk.type === 'content') {
                // Content chunk - pass to callback
                onChunk?.(chunk);
              } else if (chunk.type === 'metadata') {
                // Metadata chunk - store for final response
                finalResponse = {
                  ...finalResponse,
                  ...chunk.data
                };
                
                // Pass the entire metadata chunk to the onChunk handler
                // so the parent component can process it as needed.
                onChunk?.(chunk);
              } else if (chunk.type === 'token_usage') {
                // Token usage chunk - pass to callback but don't display in chat
                onChunk?.(chunk);
              } else if (chunk.type === 'error') {
                // Error chunk - handle error
                throw new Error(chunk.data.message || 'Stream error');
              } else if (chunk.type === 'end') {
                // End of stream marker
                finalResponse = {
                  ...finalResponse,
                  ...chunk.data
                };
                break;
              } else {
                // Unknown chunk type - log but don't display
                console.log('Unknown chunk type:', chunk.type);
              }
              
              // Move to next chunk
            } catch (parseError) {
              // Log parse errors but continue processing
              console.error('Error parsing chunk:', parseError);
            }
          }
          
          // Remove processed content from buffer
          if (processedToIndex > 0) {
            buffer = buffer.substring(processedToIndex);
          }
          
          // If we have leftover data that doesn't parse as JSON, try to handle as plain text
          if (buffer.length > 0 && !buffer.includes('{')) {
            onChunk?.({ type: 'content', data: { content: buffer } });
            buffer = '';
          }
          
          // Add to complete response for fallback
          completeResponse += chunkText;
        } catch (streamError) {
          const { type, message, retryable } = classifyStreamingError(streamError);
          
          if (type === ERROR_TYPES.NETWORK && retryable && retryAttempt < RETRY_DELAYS.length) {
            // Network error with retry possibility
            onError?.({ type, message, retryable: true, retryAttempt });
            return null;
          }
          
          throw streamError; // Re-throw other errors
        }
      }

      // Streaming complete
      setIsStreaming(false);
      setStreamingMessageId(null);
      setRetryAttempt(0);

      // If we have a final response from metadata/end chunks, use that
      if (finalResponse) {
        // Update token usage if available and not already handled by chunks
        if (finalResponse.token_usage && !finalResponse._token_usage_handled) {
          updateTokenUsage?.(finalResponse.token_usage);
          // Mark as handled to avoid double-counting
          finalResponse._token_usage_handled = true;
        }
        
        // Call onComplete with the final response
        onComplete?.(finalResponse, messageId);
        return finalResponse;
      }

      // Fallback: Try to parse the complete response as JSON
      try {
        const jsonResponse = JSON.parse(completeResponse);
        
        // Update token usage if available and not already handled by chunks
        if (jsonResponse.token_usage && !jsonResponse._token_usage_handled) {
          updateTokenUsage?.(jsonResponse.token_usage);
          // Mark as handled to avoid double-counting
          jsonResponse._token_usage_handled = true;
        }
        
        // Call onComplete with the final response
        onComplete?.(jsonResponse, messageId);
        return jsonResponse;
      } catch (finalParseError) {
        // If we can't parse as JSON, just return the complete text
        onComplete?.({ message: completeResponse }, messageId);
        return { message: completeResponse };
      }
    } catch (error) {
      const { type, message, retryable } = classifyStreamingError(error);
      
      logStreamingError(error, {
        type,
        endpoint,
        retryAttempt,
        messageId: streamingMessageId
      });

      // Don't show error for aborted requests
      if (error.name !== "AbortError") {
        onError?.({
          type,
          message,
          retryable,
          retryAttempt,
          status: error.status,
          payload: error.payload,
        });
        showSnackbar(message, "error");
      }
      
      setIsStreaming(false);
      setStreamingMessageId(null);
      return null;
    }
  }, [showSnackbar, updateTokenUsage, retryAttempt]);

  // Define retryRequest after streamChat to avoid the circular dependency
  const retryRequest = useCallback(async () => {
    if (!lastRequestRef.current || retryAttempt >= RETRY_DELAYS.length) {
      return null;
    }

    setRetryAttempt((prev) => prev + 1);
    const delay = RETRY_DELAYS[retryAttempt];

    // Wait for the backoff delay
    await new Promise(resolve => setTimeout(resolve, delay));

    // Retry the request with the same parameters
    const { endpoint, headers, body, onStart, onChunk, onComplete, onError } = lastRequestRef.current;
    return streamChat({
      endpoint,
      headers,
      body,
      onStart,
      onChunk,
      onComplete,
      onError,
      isRetry: true
    });
  }, [retryAttempt, streamChat]); // streamChat is now defined before this

  return {
    isStreaming,
    streamingMessageId,
    streamChat,
    stopStreaming,
    retryRequest,
    retryAttempt,
  };
};

export default useStreamingChat;
